// src/components/UploadForm.js
import React, { useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import useUpload from '../hooks/useUpload';
import { sendToBackend } from '../utils/debug';
import './UploadForm.css'; // ensure styles are applied
import { API_BASE, authHeader } from '../utils/api'; // (ensure authHeader is imported)

export default function UploadForm() {
  async function fetchLimits(token) {
    try {
      const res = await fetch(`${API_BASE}/account/limits`, {
        headers: authHeader(token),
      });

      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const t = await res.text();
        throw Object.assign(new Error(`GET /account/limits ${res.status}`), { status: res.status, body: t });
      }
      if (!ct.includes('application/json')) {
        const t = await res.text();
        throw new Error(`GET /account/limits non-JSON: ${t.slice(0, 160)}`);
      }
      return await res.json();
    } catch (e) {
      // If limits endpoint fails, fail open; uploader will still handle backend 403s gracefully.
      return null;
    }
  }
  const {
    token,
    setUploadedFilename,
    setCsvData,
    setColumnTypes,
    setCompanyName,
    setFinancialScale,
    setHasUploaded,
    setIsDemo,
  } = useAppContext();

  const { uploading, error, setError, uploadFile } = useUpload();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [preflighting, setPreflighting] = useState(false);
  const inputRef = useRef(null);

  function chooseFile(f) {
    if (!f) return;
    // Accept CSV by MIME or by extension (covers some browsers sending Excel MIME)
    const okMime = ['text/csv', 'application/vnd.ms-excel'].includes(f.type);
    const okExt = /\.csv$/i.test(f.name);
    if (!okMime && !okExt) {
      setError('Please upload a .csv file.');
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      chooseFile(e.dataTransfer.files[0]);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!file || uploading || preflighting) return;
    setError(null);
    // Require sign-in so we don't POST /upload without Authorization (would 422).
    if (!token) {
      setError('Please sign in to upload a CSV.');
      return;
    }

    // Start visual progress immediately for the pre-check
    setPreflighting(true);
    try {
      const limits = await fetchLimits(token);
      if (limits && typeof limits === 'object') {
        const { plan, trial_active, uploads_remaining, trial_expires_at } = limits;
        if (uploads_remaining === 0) {
          if (trial_active) {
            setError('Your trial includes 1 upload. Upgrade to Clairifi Core to continue.');
            return;
          }
          setError('Monthly upload limit reached. Upgrade or wait until your next cycle.');
          return;
        }
        if (plan !== 'core' && !trial_active && trial_expires_at) {
          setError('Your 14-day trial has ended. Upgrade to Clairifi Core to continue.');
          return;
        }
      }
    } catch (e) {
      // fail open; backend will still enforce limits
    } finally {
      // ensure the button doesn’t stick if pre-check throws
      setPreflighting(false);
    }
    // --- END NEW ---


    try {
      const res = await uploadFile(file, token);

      if (res && typeof res === 'object') {
        // If server sent a tiny preview, do NOT setCsvData with it — dashboard will rehydrate full data from backend memory.
        if (Array.isArray(res.data) && res.data.length > 0) {
          setCsvData(res.data);
        } else if (Array.isArray(res.preview)) {
          console.debug('[Upload] server-side dataset active; preview rows =', res.preview.length);
        }
        if (res.column_types) setColumnTypes(res.column_types);
        if (res.company) setCompanyName(res.company);
        if (res.unit_scale) setFinancialScale(res.unit_scale);
      }
      try { localStorage.removeItem('clairifi:forecast-cache'); } catch {}

      setUploadedFilename(file?.name || 'uploaded.csv');
      try { localStorage.setItem('clairifi:hasUploaded', '1'); } catch {}
      setHasUploaded(true);
      setIsDemo(false);
      window.location.href = '/dashboard';
    } catch (err) {
      sendToBackend('ERROR', '[UploadForm] upload failed', err);
      if (err?.status === 403) {
        const msg = (typeof err.body === 'object' && (err.body.detail || err.body.message))
          ? (err.body.detail || err.body.message)
          : (err.message || 'Your trial includes 1 upload. Upgrade to Clairifi Core to continue.');
        setError(msg);
      }
    }
  }


  return (
    <div className="upload-form-container">
      <div className="upload-form-card">
        <h1 className="upload-title">Upload financial report (CSV)</h1>
        <p className="upload-subtitle">
          Import your data to get started.
        </p>
        {error ? (
          <div className="upload-error" role="alert">
            {typeof error === 'string' ? error : (error?.message || JSON.stringify(error))}
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="upload-form" noValidate>
          {/* Hidden native input (for screen readers & programmatic click) */}
          <input
            ref={inputRef}
            className="upload-input-hidden"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => chooseFile(e.target.files?.[0] || null)}
            aria-label="Choose CSV file"
          />

          {/* Drag & Drop Zone */}
          <div
            className={`upload-dropzone ${dragActive ? 'is-dragging' : ''} ${file ? 'has-file' : ''}`}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
            }}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            aria-label="Drag and drop CSV here or click to browse"
          >
            <svg
              className="upload-icon"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 16.5a3.5 3.5 0 01-3.5 3.5h-9A3.5 3.5 0 014 16.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>

            {!file ? (
              <>
                <p className="dz-title">Drag & drop your CSV</p>
                <p className="dz-subtitle">
                  or <span className="dz-browse">browse</span> to choose a file
                </p>
                <p className="dz-hint">Accepted: .csv • Tip: first row should be column headers</p>
              </>
            ) : (
              <div className="file-chip" title={file.name}>
                <span className="file-name">{file.name}</span>
                <span className="file-size">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
                <button
                  type="button"
                  className="chip-x"
                  aria-label="Remove file"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  disabled={uploading}
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="upload-actions">
            <button
              type="button"
              className="ghost-button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              Choose file
            </button>

            <button
              type="submit"
              className="upload-button"
              disabled={!file || uploading || preflighting}
            >
              {(uploading || preflighting) && <span className="spinner" aria-hidden="true" />}
              {preflighting ? 'Checking…' : (uploading ? 'Uploading…' : 'Upload')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
